import * as React from 'react'

import type { CommentData } from '~publish/gql/graphql'

import type { FilterStatus } from '../../CommentsFilters/types'

type UseCommentAutoExpansionProps = {
  comments: CommentData[]
  filters?: { status?: FilterStatus }
  channelId?: string
}

type UseCommentAutoExpansionReturn = {
  expandedCommentId: string | null
  expandNextComment: () => void
  setExpandedCommentId: (commentId: string | null) => void
}

/**
 * Hook to manage comment auto-expansion using index-based tracking.
 *
 * This hook automatically expands the first comment when viewing unanswered
 * comments (status === 'unreplied' or 'new') and provides functionality to
 * expand the next comment sequentially by incrementing an index.
 *
 * **Key Features:**
 * - Resets expansion state when switching channels or changing filters
 * - Handles bounds checking to prevent out-of-range index access
 * - Loops back to first comment when reaching the end of the list
 * - Only active for unanswered comment filters (unreplied/new)
 *
 * @param comments - Array of comments to manage expansion for
 * @param filters - Current filter state, used to determine if auto-expansion should be active
 * @param channelId - Channel identifier to reset expansion when switching channels
 * @returns Expansion state and callback to expand next comment
 *
 * @example
 * ```tsx
 * const { expandedCommentId, expandNextComment } = useCommentAutoExpansion({
 *   comments,
 *   filters,
 *   channelId: channel.id
 * })
 *
 * // Check if a comment should be expanded
 * const isExpanded = comment.id === expandedCommentId
 *
 * // Expand next comment after reply
 * const handleReply = () => {
 *   expandNextComment()
 * }
 * ```
 */
export const useCommentAutoExpansion = ({
  comments,
  filters,
  channelId,
}: UseCommentAutoExpansionProps): UseCommentAutoExpansionReturn => {
  const [expandedIndex, setExpandedIndex] = React.useState(0)

  // Only enable auto-expansion for unanswered comments (unreplied/new)
  const shouldAutoExpand = React.useMemo(() => {
    return filters?.status === 'unreplied' || filters?.status === 'new'
  }, [filters?.status])

  // Reset expansion when filter changes OR channel changes
  React.useEffect(() => {
    setExpandedIndex(0)
  }, [filters?.status, channelId])

  const expandNextComment = React.useCallback(() => {
    if (!shouldAutoExpand) return
    setExpandedIndex((prev) => {
      const nextIndex = prev + 1
      // If we've reached the end, reset to 0 (first comment)
      return nextIndex >= comments.length ? 0 : nextIndex
    })
  }, [shouldAutoExpand, comments.length])

  // Ensure index is within bounds and reset if needed
  const safeExpandedIndex = expandedIndex >= comments.length ? 0 : expandedIndex

  // Return expanded comment ID for both auto-expansion and manual expansion
  const expandedCommentId = shouldAutoExpand
    ? comments[safeExpandedIndex]?.id || null
    : null

  // Function to manually set the expanded comment (for manual user interactions)
  const setExpandedCommentId = React.useCallback(
    (commentId: string | null) => {
      if (commentId === null) {
        // Reset to first comment
        setExpandedIndex(0)
      } else {
        // Find the index of the comment and set it
        const commentIndex = comments.findIndex(
          (comment) => comment.id === commentId,
        )
        if (commentIndex !== -1) {
          setExpandedIndex(commentIndex)
        }
      }
    },
    [comments],
  )

  return {
    expandedCommentId,
    expandNextComment,
    setExpandedCommentId,
  }
}
